/**
 * The side panel for the current selection: the editable node or arrow form
 * when the map can be edited, the read-only details otherwise. It floats over
 * the canvas's right edge on a wide map, sits beside the table in table view,
 * and drops below either on a narrow one (liveCss.ts, container queries).
 *
 * The body is keyed by the selection, so switching to another node starts its
 * fields fresh instead of showing the previous node's half-typed text (a draft
 * is committed on blur before the switch, see CommitText).
 */
import { useId, type ReactElement } from "react";
import type { FlowSelection } from "./canvasModel.js";
import { EdgeInspector } from "./EdgeInspector.js";
import { IconCrosshair, IconX } from "./icons.js";
import { IconButton, type EditorEnv } from "./inspectorFields.js";
import type { FlowView } from "./model.js";
import { EdgeDetails, NodeDetails, type LinkHandlers } from "./NodeDetails.js";
import { NodeInspector } from "./NodeInspector.js";

export interface InspectorProps {
  env: EditorEnv;
  selection: FlowSelection;
  editable: boolean;
  view: FlowView;
  links: LinkHandlers;
  onSelect: (sel: FlowSelection | null) => void;
  /** Table view: switch to the canvas and centre this node. */
  onShowOnMap: (id: string) => void;
  /** Focus the title field (the node was just added). */
  focusTitle: boolean;
}

export function Inspector(p: InspectorProps): ReactElement | null {
  const titleId = useId();
  const { env, selection } = p;
  const { labels } = env;
  const close = (): void => p.onSelect(null);

  let body: ReactElement | null = null;
  let nodeId: string | null = null;
  if (selection.type === "node") {
    const node = env.doc.nodes.find((n) => n.id === selection.id);
    if (node) {
      nodeId = node.id;
      body = p.editable ? (
        <NodeInspector env={env} node={node} autoFocusTitle={p.focusTitle} onDeleted={close} />
      ) : (
        <NodeDetails doc={env.doc} node={node} labels={labels} locale={env.locale} handlers={p.links} />
      );
    }
  } else {
    const edge = env.doc.edges.find((e) => e.id === selection.id);
    if (edge) {
      body = p.editable ? (
        <EdgeInspector env={env} edge={edge} onSelect={p.onSelect} onDeleted={close} />
      ) : (
        <EdgeDetails doc={env.doc} edge={edge} labels={labels} locale={env.locale} onGoToNode={p.links.onGoToNode} />
      );
    }
  }
  if (!body) return null;

  return (
    <aside className="wa-fm__inspector" aria-labelledby={titleId}>
      <div className="wa-fm__inspector-head">
        <div className="wa-fm__inspector-title" id={titleId}>
          {labels.inspector}
        </div>
        {nodeId && p.view === "table" ? (
          <IconButton label={labels.goToNode} onClick={() => p.onShowOnMap(nodeId)}>
            <IconCrosshair size={15} />
          </IconButton>
        ) : null}
        <IconButton label={labels.close} onClick={close}>
          <IconX size={15} />
        </IconButton>
      </div>
      <div className="wa-fm__inspector-body" key={`${selection.type}:${selection.id}`}>
        {body}
      </div>
    </aside>
  );
}
